import { Building2, CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { HomeDashboardCard } from "../../lib/accessRequests";
import { resolveAccessLevel } from "../../lib/accessRequests";
import { formatCurrency } from "../../lib/utils";
import { ModuleAccessGate } from "./ModuleAccessGate";
import { StatCard } from "../ui/StatCard";

const MODULE_ICONS = {
  apartments: Building2,
  payments: CreditCard,
} as const;

export function HomeModuleCard({ card }: { card: HomeDashboardCard }) {
  const { t } = useTranslation();
  const title = t(`accessRequests.modules.${card.module}`, { defaultValue: card.module });
  const icon = MODULE_ICONS[card.module as keyof typeof MODULE_ICONS] ?? Building2;

  if (resolveAccessLevel(card) === "none") {
    return (
      <ModuleAccessGate title={title} moduleKey={card.module} marker={card}>
        <div className="hidden" aria-hidden />
      </ModuleAccessGate>
    );
  }

  if (card.module === "apartments" && card.summary && "total" in card.summary) {
    const s = card.summary;
    return (
      <div className="card p-6 space-y-4" data-testid={`home-card-${card.module}`}>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary-600" aria-hidden />
          {title}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <StatCard label={t("dashboard.apartments.total")} value={s.total} icon={icon} />
          <StatCard label={t("dashboard.apartments.occupied")} value={s.occupied} icon={icon} />
          <StatCard label={t("dashboard.apartments.vacant")} value={s.vacant} icon={icon} />
          <StatCard label={t("dashboard.apartments.overdue")} value={s.overdue} icon={icon} />
        </div>
      </div>
    );
  }

  if (card.module === "payments" && card.summary && "pending_count" in card.summary) {
    const s = card.summary;
    const currency = s.currency || "RON";
    return (
      <div className="card p-6 space-y-4" data-testid={`home-card-${card.module}`}>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-primary-600" aria-hidden />
          {title}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t("dashboard.pendingPayments")}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{s.pending_count}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {formatCurrency(Number(s.pending_total), currency)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t("dashboard.overduePayments")}</p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">{s.overdue_count}</p>
            <p className="text-sm text-red-600/90 dark:text-red-400/90">
              {formatCurrency(Number(s.overdue_total), currency)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
